import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Chip,
    Tabs,
    Tab,
    Collapse,
    useTheme,
    useMediaQuery,
    alpha,
} from '@mui/material';
import {
    CheckCircle as CheckIcon,
    Cancel as XIcon,
    InfoOutlined as InfoIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import { usePermissions } from '../../../hooks/usePermissions';
import { fetchDashboard } from './services/usageDashboardService';
import { daysAgo, formatDaysAgo } from '../../utils/dateUtils';

// ─── Configs ─────────────────────────────────────────────────────────────────

const ACTIVITY_LEVEL = {
    HIGH: { label: 'High', color: '#10B981', bg: '#ECFDF5' },
    MEDIUM: { label: 'Medium', color: '#3B82F6', bg: '#EFF6FF' },
    LOW: { label: 'Low', color: '#F59E0B', bg: '#FFFBEB' },
    NONE: { label: 'None', color: '#9CA3AF', bg: '#F9FAFB' },
};

// ─── Table styles ────────────────────────────────────────────────────────────

const denseCell = {
    py: 0.6,
    px: 1,
    fontSize: '0.8rem',
    borderBottom: '1px solid',
    borderColor: 'grey.100',
    whiteSpace: 'nowrap',
};

const headerCell = {
    ...denseCell,
    fontWeight: 600,
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    borderBottom: '2px solid',
    borderColor: 'grey.200',
    py: 0.75,
    whiteSpace: 'normal',
    lineHeight: 1.3,
    verticalAlign: 'bottom',
};

// ─── Shared sub-components ───────────────────────────────────────────────────

const SmallChip = ({ status, config }) => {
    const c = config[status] || config.NONE;
    return (
        <Chip
            label={c.label}
            size="small"
            sx={{
                fontWeight: 600,
                fontSize: '0.68rem',
                height: 20,
                color: c.color,
                bgcolor: c.bg,
                border: 'none',
                '& .MuiChip-label': { px: 1 },
            }}
        />
    );
};

const SetupIcon = ({ value, count }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
        {value ? (
            <CheckIcon sx={{ fontSize: 15, color: '#10B981' }} />
        ) : (
            <XIcon sx={{ fontSize: 15, color: '#D1D5DB' }} />
        )}
        {value && count != null && count > 0 && (
            <Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                {count}
            </Typography>
        )}
    </Box>
);

const NumCell = ({ value, color }) => (
    <Typography
        component="span"
        sx={{
            fontWeight: value > 0 ? 600 : 400,
            fontSize: '0.8rem',
            color: value > 0 ? color || 'text.primary' : 'text.secondary',
        }}
    >
        {value}
    </Typography>
);

// ─── Combined Project Table ─────────────────────────────────────────────────

const ProjectTable = ({ projects, isMobile, theme }) => {
    if (!projects?.length) {
        return (
            <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No active projects found.
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ ...headerCell, minWidth: 130 }}>Project</TableCell>
                        <TableCell sx={{ ...headerCell, textAlign: 'center' }}>
                            Floor
                            <br />
                            Plans
                        </TableCell>
                        <TableCell sx={{ ...headerCell, textAlign: 'center' }}>Rooms</TableCell>
                        {!isMobile && (
                            <>
                                <TableCell sx={{ ...headerCell, textAlign: 'center' }}>
                                    Internal
                                    <br />
                                    PMs
                                </TableCell>
                                <TableCell sx={{ ...headerCell, textAlign: 'center' }}>
                                    Internal
                                    <br />
                                    Supers
                                </TableCell>
                                <TableCell sx={{ ...headerCell, textAlign: 'center' }}>
                                    External
                                    <br />
                                    Owners
                                </TableCell>
                                <TableCell sx={{ ...headerCell, textAlign: 'center' }}>
                                    External
                                    <br />
                                    Consultants
                                </TableCell>
                                <TableCell sx={{ ...headerCell, textAlign: 'center' }}>
                                    External
                                    <br />
                                    Sub PMs
                                </TableCell>
                            </>
                        )}
                        <TableCell sx={{ ...headerCell, textAlign: 'center' }}>
                            External
                            <br />
                            Access
                        </TableCell>
                        <TableCell sx={{ ...headerCell, textAlign: 'right' }}>
                            Deficiency
                            <br />
                            Count
                        </TableCell>
                        <TableCell sx={{ ...headerCell, textAlign: 'right' }}>
                            Last
                            <br />
                            Activity
                        </TableCell>
                        <TableCell sx={{ ...headerCell, textAlign: 'center' }}>
                            Activity
                            <br />
                            Level
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {projects.map((p) => {
                        const days = daysAgo(p.lastActivityDate);
                        const isStale = days !== null && days > 14;
                        return (
                            <TableRow key={p.projectId} hover>
                                <TableCell sx={{ ...denseCell, fontWeight: 500 }}>
                                    <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
                                        {p.projectName}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={denseCell} align="center">
                                    <SetupIcon
                                        value={p.floorplanCount > 0}
                                        count={p.floorplanCount}
                                    />
                                </TableCell>
                                <TableCell sx={denseCell} align="center">
                                    <SetupIcon value={p.roomCount > 0} count={p.roomCount} />
                                </TableCell>
                                {!isMobile && (
                                    <>
                                        <TableCell sx={denseCell} align="center">
                                            <SetupIcon value={p.hasInternalPm} />
                                        </TableCell>
                                        <TableCell sx={denseCell} align="center">
                                            <SetupIcon value={p.hasInternalSuper} />
                                        </TableCell>
                                        <TableCell sx={denseCell} align="center">
                                            <SetupIcon value={p.hasExternalOwner} />
                                        </TableCell>
                                        <TableCell sx={denseCell} align="center">
                                            <SetupIcon value={p.hasExternalConsultant} />
                                        </TableCell>
                                        <TableCell sx={denseCell} align="center">
                                            <SetupIcon value={p.hasExternalSubcontractorPm} />
                                        </TableCell>
                                    </>
                                )}
                                <TableCell sx={denseCell} align="center">
                                    <SetupIcon value={false} />
                                </TableCell>
                                <TableCell sx={denseCell} align="right">
                                    <NumCell value={p.totalDeficiencies} />
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...denseCell,
                                        color: isStale
                                            ? theme.palette.status?.error || '#EF4444'
                                            : 'text.secondary',
                                        fontWeight: isStale ? 600 : 400,
                                    }}
                                    align="right"
                                >
                                    {formatDaysAgo(p.lastActivityDate)}
                                </TableCell>
                                <TableCell sx={denseCell} align="center">
                                    <SmallChip status={p.activityLevel} config={ACTIVITY_LEVEL} />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// ─── Section 3: User Adoption Table ──────────────────────────────────────────

const UserTable = ({ users, theme, isMobile, isTablet }) => {
    const [sort, setSort] = useState({ field: null, dir: 'asc' });

    const handleSort = (field) => {
        setSort((prev) => ({
            field,
            dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sorted = useMemo(() => {
        if (!users?.length) return [];
        const list = [...users];
        if (sort.field) {
            list.sort((a, b) => {
                let aVal = a[sort.field];
                let bVal = b[sort.field];
                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return 1;
                if (bVal == null) return -1;
                if (sort.field === 'lastActivityDate') {
                    aVal =
                        typeof aVal === 'string' && aVal.match(/^\d{4}-\d{2}-\d{2}$/)
                            ? new Date(`${aVal}T12:00:00Z`).getTime()
                            : new Date(aVal).getTime();
                    bVal =
                        typeof bVal === 'string' && bVal.match(/^\d{4}-\d{2}-\d{2}$/)
                            ? new Date(`${bVal}T12:00:00Z`).getTime()
                            : new Date(bVal).getTime();
                }
                if (aVal < bVal) return sort.dir === 'asc' ? -1 : 1;
                if (aVal > bVal) return sort.dir === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return list;
    }, [users, sort]);

    const SortHeader = ({ field, children, align }) => (
        <TableCell sx={headerCell} align={align || 'right'}>
            <TableSortLabel
                active={sort.field === field}
                direction={sort.field === field ? sort.dir : 'asc'}
                onClick={() => handleSort(field)}
                sx={{ '& .MuiTableSortLabel-icon': { fontSize: '0.8rem' } }}
            >
                {children}
            </TableSortLabel>
        </TableCell>
    );

    if (!sorted.length) {
        return (
            <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No users found.
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={headerCell}>User</TableCell>
                        {!isTablet && <TableCell sx={headerCell}>Projects</TableCell>}
                        <SortHeader field="activityLast7Days">Activity</SortHeader>
                        <SortHeader field="deficienciesCreatedLast7Days">
                            Defs
                            <br />
                            Created
                        </SortHeader>
                        {!isMobile && (
                            <SortHeader field="loginDaysLast7Days">
                                Login
                                <br />
                                Days
                            </SortHeader>
                        )}
                        {!isMobile && (
                            <SortHeader field="lastActivityDate">
                                Last
                                <br />
                                Activity
                            </SortHeader>
                        )}
                        <TableCell sx={headerCell} align="center">
                            Activity
                            <br />
                            Level
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map((user) => (
                        <TableRow
                            key={user.userId}
                            sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                        >
                            <TableCell sx={{ ...denseCell, fontWeight: 500 }}>
                                {user.userName}
                            </TableCell>
                            {!isTablet && (
                                <TableCell
                                    sx={{
                                        ...denseCell,
                                        maxWidth: 180,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    {user.projectNames?.join(', ') || '—'}
                                </TableCell>
                            )}
                            <TableCell sx={denseCell} align="right">
                                <NumCell value={user.activityLast7Days} />
                            </TableCell>
                            <TableCell sx={denseCell} align="right">
                                <NumCell value={user.deficienciesCreatedLast7Days} />
                            </TableCell>
                            {!isMobile && (
                                <TableCell sx={denseCell} align="right">
                                    <NumCell
                                        value={user.loginDaysLast7Days}
                                        color={theme.palette.status?.success || '#10B981'}
                                    />
                                </TableCell>
                            )}
                            {!isMobile && (
                                <TableCell
                                    sx={{
                                        ...denseCell,
                                        color:
                                            daysAgo(user.lastActivityDate) > 14
                                                ? theme.palette.status?.error || '#EF4444'
                                                : 'text.secondary',
                                    }}
                                    align="right"
                                >
                                    {formatDaysAgo(user.lastActivityDate)}
                                </TableCell>
                            )}
                            <TableCell sx={denseCell} align="center">
                                <SmallChip status={user.userStatus} config={ACTIVITY_LEVEL} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// ─── Collapsible Metric Definitions ──────────────────────────────────────────

const MetricDefinitions = () => {
    const [open, setOpen] = useState(false);
    return (
        <Paper
            elevation={0}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
            }}
        >
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    px: 2,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.50' },
                    userSelect: 'none',
                }}
            >
                <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.78rem', fontWeight: 500 }}
                >
                    How metrics are calculated
                </Typography>
                <ExpandMoreIcon
                    sx={{
                        fontSize: 16,
                        color: 'text.secondary',
                        ml: 'auto',
                        transform: open ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                    }}
                />
            </Box>
            <Collapse in={open}>
                <Box
                    sx={{
                        px: 2,
                        pb: 1.5,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 3,
                    }}
                >
                    {/* Project metrics */}
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                color: 'text.secondary',
                                display: 'block',
                                mb: 0.5,
                            }}
                        >
                            Project Metrics
                        </Typography>
                        <DefLine
                            term="External Access"
                            desc="Whether external users have been granted access to this project"
                        />
                        <DefLine
                            term="Deficiency Count"
                            desc="Total number of deficiencies on the project"
                        />
                        <DefLine
                            term="Last Activity"
                            desc="Most recent date any action was taken"
                        />
                        <DefLine
                            term="Activity Level"
                            desc="High: 5 or more deficiency changes in the last 7 days. Medium: any deficiency changes in the last 7 days. Low: activity in the last 7 - 14 days. None: no activity in over 14 days."
                        />
                    </Box>
                    {/* User metrics */}
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                color: 'text.secondary',
                                display: 'block',
                                mb: 0.5,
                            }}
                        >
                            User Metrics
                        </Typography>
                        <DefLine
                            term="Activity"
                            desc="Total actions in the last 7 days, including deficiencies created, deficiencies modified, after photos uploaded, and comments posted"
                        />
                        <DefLine
                            term="Deficiencies Created"
                            desc="Number of new deficiencies the user created in the last 7 days"
                        />
                        <DefLine
                            term="Login Days"
                            desc="Number of days the user logged in during the last 7 days"
                        />
                        <DefLine
                            term="Last Activity"
                            desc="Most recent date any action was taken"
                        />
                        <DefLine
                            term="Activity Level"
                            desc="High: 5 or more total actions or 3 or more login days in the last 7 days. Medium: any action or any login in the last 7 days. Low: activity in the last 7-14 days. None: no activity in over 14 days."
                        />
                    </Box>
                </Box>
            </Collapse>
        </Paper>
    );
};

const DefLine = ({ term, desc }) => (
    <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}
    >
        <strong>{term}:</strong> {desc}
    </Typography>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const UsageDashboardPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { businessId } = usePermissions();

    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userTab, setUserTab] = useState(0);

    const loadDashboard = useCallback(async () => {
        if (!businessId) return;
        try {
            setLoading(true);
            setError(null);
            const data = await fetchDashboard(businessId);
            setDashboard(data);
        } catch (err) {
            console.error('Failed to load dashboard:', err);
            setError('Failed to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const currentUsers = useMemo(() => {
        if (!dashboard) return [];
        return userTab === 0 ? dashboard.internalUsers || [] : dashboard.externalUsers || [];
    }, [dashboard, userTab]);

    const internalCount = dashboard?.internalUsers?.length || 0;
    const externalCount = dashboard?.externalUsers?.length || 0;

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
            {/* Page header */}
            <Box sx={{ mb: 2.5 }}>
                <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ fontSize: { xs: '1.15rem', sm: '1.35rem' } }}
                >
                    Rollout Tracker
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25, fontSize: '0.8rem' }}
                >
                    Setup completion and adoption across all active projects
                </Typography>
            </Box>

            {/* Projects — Setup + Activity combined */}
            {dashboard?.projectRollout?.length > 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        mb: 2,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.25,
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ fontSize: '0.88rem' }}
                        >
                            Projects
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: { xs: 1, sm: 2 },
                                alignItems: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                }}
                            >
                                <CheckIcon sx={{ fontSize: 13, color: '#10B981' }} />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem' }}
                                >
                                    Added
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                }}
                            >
                                <XIcon sx={{ fontSize: 13, color: '#D1D5DB' }} />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem' }}
                                >
                                    Missing
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    ml: { xs: 0, sm: 1 },
                                }}
                            >
                                {['HIGH', 'MEDIUM', 'LOW', 'NONE'].map((level) => (
                                    <Chip
                                        key={level}
                                        label={ACTIVITY_LEVEL[level].label}
                                        size="small"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.6rem',
                                            height: 16,
                                            color: ACTIVITY_LEVEL[level].color,
                                            bgcolor: ACTIVITY_LEVEL[level].bg,
                                            border: 'none',
                                            '& .MuiChip-label': { px: 0.5 },
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                    <ProjectTable
                        projects={dashboard.projectRollout}
                        isMobile={isMobile}
                        theme={theme}
                    />
                </Paper>
            )}

            {/* Section 3: User Adoption */}
            {dashboard && (
                <Paper
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        mb: 2,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            px: 2,
                            py: 1.25,
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ fontSize: '0.88rem' }}
                        >
                            User Adoption
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.72rem' }}
                        >
                            All stats below cover the last 7 days unless noted otherwise
                        </Typography>
                    </Box>
                    <Tabs
                        value={userTab}
                        onChange={(_e, v) => setUserTab(v)}
                        sx={{
                            px: 2,
                            minHeight: 36,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                minHeight: 36,
                                py: 0.5,
                                px: 1.5,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                minWidth: 'auto',
                            },
                            '& .MuiTabs-indicator': { height: 2 },
                        }}
                    >
                        <Tab label={`Internal (${internalCount})`} />
                        <Tab label={`External (${externalCount})`} />
                    </Tabs>
                    <UserTable
                        users={currentUsers}
                        theme={theme}
                        isMobile={isMobile}
                        isTablet={isTablet}
                    />
                </Paper>
            )}

            {/* Collapsible metric definitions */}
            <MetricDefinitions />
        </Container>
    );
};

export default UsageDashboardPage;
